import React, { useState } from 'react';
import { LuCloudUpload, LuFileCheck, LuFileText, LuTrash2 } from 'react-icons/lu';

export const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Presentation-only dropzone. It never validates files itself: clicks open the
 * caller's hidden <input type="file"> and dropped files are forwarded to the
 * same handler the input uses, so accepted types / validation stay unchanged.
 */
const FileDropzone = ({
  inputRef,
  onFiles,
  disabled = false,
  invalid = false,
  fileCount = 0,
  title = 'Drop your files here',
  actionLabel = 'browse',
  hint,
  formats = [],
  describedBy,
}) => {
  const [dragging, setDragging] = useState(false);

  const open = () => {
    if (!disabled) inputRef?.current?.click();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };

  const onDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragging) setDragging(true);
  };

  const onDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = e.dataTransfer?.files;
    if (files && files.length && typeof onFiles === 'function') onFiles(files);
  };

  const cls = [
    'ob-dropzone',
    dragging ? 'is-dragging' : '',
    disabled ? 'is-disabled' : '',
    invalid ? 'is-invalid' : '',
    fileCount > 0 ? 'has-files' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cls}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-describedby={describedBy}
      onClick={open}
      onKeyDown={onKeyDown}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <span className="ob-dropzone__icon" aria-hidden="true">
        {fileCount > 0 ? <LuFileCheck size={22} /> : <LuCloudUpload size={22} />}
      </span>
      <p className="ob-dropzone__title">
        {fileCount > 0 ? (
          <>
            {fileCount} file{fileCount === 1 ? '' : 's'} ready · <span>add more</span>
          </>
        ) : (
          <>
            {title} or <span>{actionLabel}</span>
          </>
        )}
      </p>
      {hint && <p className="ob-dropzone__hint">{hint}</p>}
      {formats.length > 0 && (
        <span className="ob-dropzone__formats">
          {formats.map((f) => (
            <span key={f} className="ob-badge">
              {f}
            </span>
          ))}
        </span>
      )}
    </div>
  );
};

export const FileList = ({ files = [], onRemove, disabled = false, title }) => {
  if (!files.length) return null;
  return (
    <>
      {title && (
        <div className="ob-file-list__head">
          <span>{title}</span>
          <span className="ob-muted">{files.length}</span>
        </div>
      )}
      <ul className="ob-file-list">
        {files.map((f) => {
          const isPdf = f.type === 'pdf' || /\.pdf$/i.test(f.name || '');
          return (
            <li key={f.id || f.name} className="ob-file">
              <span className={`ob-file__icon ${isPdf ? 'ob-file__icon--pdf' : ''}`} aria-hidden="true">
                <LuFileText size={17} />
              </span>
              <div className="ob-file__meta">
                <p className="ob-file__name" title={f.name}>
                  {f.name}
                </p>
                <p className="ob-file__sub">
                  {isPdf ? 'PDF' : 'DOCX'}
                  {Number.isFinite(f.size) ? ` · ${formatFileSize(f.size)}` : ''}
                </p>
              </div>
              {onRemove && (
                <button
                  type="button"
                  className="ob-file__remove"
                  onClick={() => onRemove(f.id)}
                  disabled={disabled}
                  aria-label={`Remove ${f.name}`}
                  title="Remove file"
                >
                  <LuTrash2 size={16} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default FileDropzone;
